import { useDispatch } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import {
  ADVISOR_LOGO_EXTENSION_BY_CONTENT_TYPE,
  AdvisorLogoContentType,
  MAX_ADVISOR_LOGO_SIZE_BYTES,
} from '@shared';
import { advisorLogoApi } from '@client/api/waterlily';
import { uploadFileToS3 } from '@client/util/uploadFileToBucket';
import { fetchSessionForAuthRequest } from '@client/model';

const MAX_LOGO_SIZE_MB = MAX_ADVISOR_LOGO_SIZE_BYTES / (1024 * 1024);

export function useAdvisorLogoUpload(onLogoKeyChange?: (advisorLogoKey: string | null) => void) {
  const dispatch = useDispatch();

  const refreshSession = () => dispatch(fetchSessionForAuthRequest({ invalidating: true }));

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!(file.type in ADVISOR_LOGO_EXTENSION_BY_CONTENT_TYPE)) {
        throw new Error('Please choose a PNG or SVG image.');
      }
      if (file.size > MAX_ADVISOR_LOGO_SIZE_BYTES) {
        throw new Error(`Logo must be under ${MAX_LOGO_SIZE_MB}MB.`);
      }
      const contentType = file.type as AdvisorLogoContentType;
      const { url, advisorLogoKey } = await advisorLogoApi.createUpload(contentType);
      await uploadFileToS3(
        url,
        file,
        contentType,
        // Signed into the presigned URL by postAdvisorLogoUpload for SVGs
        contentType === 'image/svg+xml' ? { 'Content-Disposition': 'attachment' } : undefined,
      );
      return advisorLogoKey;
    },
    onSuccess: advisorLogoKey => {
      refreshSession();
      onLogoKeyChange?.(advisorLogoKey);
    },
  });

  const removeMutation = useMutation({
    mutationFn: advisorLogoApi.remove,
    onSuccess: () => {
      refreshSession();
      onLogoKeyChange?.(null);
    },
  });

  return {
    uploadLogo: uploadMutation.mutate,
    removeLogo: removeMutation.mutate,
    isSaving: uploadMutation.isPending || removeMutation.isPending,
    error: uploadMutation.error ?? removeMutation.error,
  };
}
